import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import uselabelApi from "../../http/label/api";
import { Label, UpsertLabel } from "../../http/label/types";
import { PaginationQuery } from "../../types";
import { formatPrice } from "../../utils";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import Button from "../ui/Button";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import TH from "../ui/TH";
import LabelForm, { LabelFormBody } from "./LabelForm";

const Labels: FC = () => {
  const { useLabels: getLabels } = uselabelApi();

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const { isOpen, open, close } = useToggle();
  const [editLabel, setEditLabel] = useState<Label>();
  const [deletelabel, setDeletelabel] = useState<Label>();
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const labelApi = uselabelApi();
  const i18n = useI18n();

  const pageContext = usePageContext();

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };

  const { data, error } = getLabels(query);

  const onSubmit = async (formData: LabelFormBody) => {
    const body: UpsertLabel = {
      code: formData.code,
      cost: formData.cost,
    };
    if (!editLabel) {
      try {
        await labelApi.createLabel(body);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    } else {
      try {
        await labelApi.updateLabel(body, editLabel.id);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    }
  };

  const actions: Action<Label>[] = [
    {
      title: i18n.t.common.edit,
      onClick: (item) => {
        setEditLabel(item);
        open();
      },
      icon: SvgEdit,
    },
    {
      title: i18n.t.common.delete,
      onClick: setDeletelabel,
      color: "red",
      icon: SvgDelete,
      isDivider: true,
    },
  ];

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  useEffect(() => {
    pageContext.setPage({ title: i18n.t.labels.plural });
  }, [i18n.language]);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <>
      <Sidebar isOpen={isOpen} onClose={close} title={!editLabel ? i18n.t.labels.newLabel : i18n.t.labels.editLabel}>
        <LabelForm key={isOpen ? (editLabel?.id ?? "new") : "closed"} onSubmit={onSubmit} label={editLabel} />
      </Sidebar>
      <Table<Label>
        buttons={{
          main: (
            <Button
              buttonProps={{
                onClick: () => {
                  setEditLabel(undefined);
                  open();
                },
              }}
              glyph={SvgAdd}
            />
          ),
        }}
        onRowClick={(item) => {
          setEditLabel(item);
          open();
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        rowActions={actions}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.t.common.code}</th>
            <TH order={orderOptions} fieldName="label.cost" title={i18n.t.common.cost} />
            <th align="left"></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>{item.code}</td>
            <td>{formatPrice(item.cost, i18n.language, { min: 2, max: 4 })}</td>
          </>
        )}
        items={data?.items}
        total={data?.total}
        error={error}
      />

      {!!deletelabel && (
        <ModalDialog
          onClose={() => setDeletelabel(undefined)}
          title={i18n.t.labels.delete}
          isSubmitting={isModalSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setDeletelabel(undefined),
            },
            {
              title: i18n.t.common.yes,
              onClick: async () => {
                setIsModalSubmitting(true);
                if (!deletelabel) return;
                try {
                  await labelApi.deleteLabel(deletelabel.id);
                  setIsModalSubmitting(false);
                  setDeletelabel(undefined);
                } catch (e) {
                  setIsModalSubmitting(false);
                  dispatch(showErrorNotification(i18n, e));
                }
              },
            },
          ]}
        >
          {i18n.t.labels.questions.delete}
        </ModalDialog>
      )}
    </>
  );
};

export default Labels;
